import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { formatRelativeTime } from '../../../shared/utils/dateUtils';
import SessionDetailView from './SessionDetailView';
import { AVAILABLE_COLUMNS } from '../config/sessionColumns';
import { TABLE_STYLES } from '../styles/dataTableStyles';

const SessionRow = ({ session, isExpanded, onToggle, columns, styles: stylesProp }) => {
  const navigate = useNavigate();
  const params = useParams();
  const styles = stylesProp || TABLE_STYLES;

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const sessionDisplay = session.userIdentity?.username || session.userId || session.sessionId;
  const userId = session.userId;
  const isUserIdDisplay = !session.userIdentity?.username && userId;

  const getCellValue = (columnKey) => {
    const def = AVAILABLE_COLUMNS[columnKey];
    const field = def?.field || columnKey;
    switch (field) {
      case 'sessionId':
        return session.sessionId;
      case 'startTime':
        return formatRelativeTime(new Date(session.startTime));
      case 'userDisplay':
        return sessionDisplay;
      case 'pagesDuration':
        return `${session.pagesViewed || 0} pages in ${formatDuration(session.duration || 0)}`;
      case 'country':
        return session.country || (session.locationData && session.locationData.country) || 'Unknown';
      case 'city':
        return (session.locationData && session.locationData.city) || session.city || '';
      case 'deviceType':
        return (session.device && session.device.type) || '';
      case 'os':
        return (session.device && session.device.os) || '';
      case 'browserName':
        return (session.browser && session.browser.name) || '';
      case 'entryPage':
        return session.entry_page || session.entryPage || '';
      case 'exitPage':
        return session.exit_page || session.exitPage || '';
      case 'referrer':
        return session.referrer || '';
      default:
        return session[field] ?? '';
    }
  };

  const handleSessionDisplayClick = (e) => {
    if (isUserIdDisplay) {
      e.stopPropagation();
      const team = params.team || localStorage.getItem('selectedTeam');
      navigate(`/${team}/activity/users?userId=${encodeURIComponent(userId)}`);
    }
  };

  const getAlignmentClass = (columnKey) => {
    const def = AVAILABLE_COLUMNS[columnKey];
    const field = def?.field || columnKey;
    return field === 'sessionId' || field === 'userDisplay' ? 'text-left' : 'text-center';
  };

  const displayColumns = columns && columns.length > 0 ? columns : ['session_id', 'time', 'user', 'pages_duration', 'country'];

  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer transition-colors"
        style={{
          borderBottom: isExpanded ? 'none' : `1px solid ${styles.borderColor}`,
          backgroundColor: 'transparent'
        }}
        onMouseEnter={(e) => !isExpanded && (e.currentTarget.style.backgroundColor = styles.secondaryBg)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        {displayColumns.map((columnKey) => {
          const alignmentClass = getAlignmentClass(columnKey);
          const value = getCellValue(columnKey);
          const isUserCol = AVAILABLE_COLUMNS[columnKey] && AVAILABLE_COLUMNS[columnKey].field === 'userDisplay';
          return (
            <td key={columnKey} className={`px-6 py-3 whitespace-nowrap ${alignmentClass}`}>
              <div className="text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                {isUserCol && isUserIdDisplay ? (
                  <span
                    className="cursor-pointer hover:underline"
                    style={{ color: '#2563eb' }}
                    onClick={handleSessionDisplayClick}
                    title={sessionDisplay}
                  >
                    {value || '—'}
                  </span>
                ) : (
                  <span title={typeof value === 'string' ? value : ''}>{value || '—'}</span>
                )}
              </div>
            </td>
          );
        })}
        <td className="px-6 py-3 whitespace-nowrap text-right">
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 inline" style={{ color: styles.textPrimary }} />
          ) : (
            <ChevronDown className="h-4 w-4 inline" style={{ color: styles.textSecondary }} />
          )}
        </td>
      </tr>

      {isExpanded && (
        <tr>
          <td
            colSpan={displayColumns.length + 1}
            style={{ borderBottom: `1px solid ${styles.borderColor}`, backgroundColor: 'transparent' }}
          >
            <div className="px-6 py-4">
              <SessionDetailView session={session} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default SessionRow;
